import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';

const ELEMENTS_722 = ['fire', 'ice', 'light', 'dark'];
const ATK_TYPES_722 = ['PATK', 'MATK', 'dynamic (higher of PATK/MATK)'];
const CROSSHAIR_COLORS_729 = ['green', 'red', 'orange', 'blue'];
const CROSSHAIR_HEX_729 = /^#([0-9a-fA-F]{6})$/;

/** Project Duck Custom (C++) state effects (700-745; there is no 707). */
export const pdCustom: Record<number, IBlowDef> = {
    700: {
        name: 'Distance Travel Check',
        wikiDesc: 'For the duration, tracks the distance traveled (XZ plane) by the actor. Used with ' +
            'bubble condition 50 (DistanceTraveled) and bubble handler 50 (Reset Distance Travel). The ' +
            'optional channel parameter names this tracker so several unrelated mechanics can track ' +
            'distance at the same time without sharing an accumulator.',
        describe() {
            return [
                'Tracks distance traveled (XZ plane) by the actor (used with bubble condition 50, ' +
                'DistanceTraveled)',
            ];
        },
    },
    701: {
        name: 'Force Use Maximum Damage Roll',
        wikiDesc: 'Attacks will always roll the max damage (instead of a Gaussian roll between min and ' +
            'max).',
        describe() {
            return ['Attacks always roll max damage (instead of a Gaussian roll between min and max)'];
        },
    },
    702: {
        name: 'Reset All Cooldowns',
        wikiDesc: "Resets all cooldowns, including passive skills, special crests, and skills that " +
            "can't normally have their cooldown refreshed.",
        describe() {
            return [
                "Resets all cooldowns, including passive skills, special crests, and skills that can't " +
                'normally have their cooldown refreshed',
            ];
        },
    },
    703: {
        name: 'Apply Multiple State Effects',
        wikiDesc: 'Applies multiple state effects to the target actor(s), formatted as a $-separated ' +
            'list of `stateEffectId;Duration;params` groups.',
        describe({ raw }) {
            const groups = raw.split('$').map((s) => s.trim()).filter((s) => s !== '');
            if (!groups.length) {
                return null;
            }
            const children: Segment[] = [];
            for (const group of groups) {
                const f = group.split(';');
                const blowId = ParamReader.num(f[0]);
                const durationMs = ParamReader.num(f[1]);
                if (isNaN(blowId) || isNaN(durationMs)) {
                    continue;
                }
                const childParams = f.slice(2).join(';');
                children.push(seg.child(blowId, childParams, durationMs));
            }
            if (!children.length) {
                return null;
            }
            const out: Segment[] = ['Applies multiple state effects to the target actor(s): '];
            children.forEach((c, i) => {
                if (i > 0) {
                    out.push(', ');
                }
                out.push(c);
            });
            return out;
        },
    },
    704: {
        name: 'Crit Forgiveness / Damage Floor',
        wikiDesc: "Increases damage of non-crits by a flat amount, or a % of the actor's crit damage.",
        describe({ params }) {
            const f = params.numbers();
            const absoluteValue = f[0];
            const ratioValue = f[1];
            if (isNaN(absoluteValue) || isNaN(ratioValue)) {
                return null;
            }
            const parts: Segment[][] = [];
            if (absoluteValue) {
                parts.push([seg.num(absoluteValue), ' flat']);
            }
            if (ratioValue) {
                parts.push([seg.pct(ratioValue), " of the actor's critical damage"]);
            }
            if (!parts.length) {
                return null;
            }
            return ['Non-critical hits deal additional damage: ', seg.list(parts)];
        },
    },
    705: {
        name: 'Zed Ult',
        wikiDesc: 'Applies a mark to the target that stores a % of self and team damage dealt to it, ' +
            'up to a cap. After the mark duration, deals the stored damage to the target.',
        describe({ params }) {
            const f = params.numbers();
            const selfPct = f[0];
            const partyPct = f[1];
            const capDamage = f[2];
            const ignoreInvincibility = f[3];
            if (isNaN(selfPct) || isNaN(partyPct) || isNaN(capDamage) || isNaN(ignoreInvincibility)) {
                return null;
            }
            const out: Segment[] = [
                'Marks the target, storing ', seg.pct(selfPct), ' of self damage and ', seg.pct(partyPct),
                ' of party damage dealt to it',
            ];
            if (capDamage >= 0) {
                out.push(', capped at ', seg.num(capDamage));
            } else {
                out.push(' (no cap)');
            }
            out.push('. When the mark expires, deals the stored damage to the target');
            if (ignoreInvincibility === 1) {
                out.push(' (ignores invincibility)');
            }
            return out;
        },
    },
    706: {
        name: 'Guaranteed Crit (Hitter)',
        wikiDesc: "Grants a guaranteed critical hit, regardless of the hitter's crit rate or the " +
            'target\'s crit resistance. -1 disables it; 0 or empty applies to all skills; a skill ID ' +
            'list restricts it to those skills.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '' || trimmed === '0') {
                return [
                    "Guarantees a critical hit on all attacks, regardless of crit rate or the target's " +
                    'crit resistance',
                ];
            }
            if (trimmed === '-1') {
                return ['Disables guaranteed crits'];
            }
            const ids = ParamReader.numList(trimmed, ';');
            if (!ids.length) {
                return null;
            }
            return [
                'Guarantees a critical hit with ', seg.list(ids.map((id) => [seg.skill(id)] as Segment[])),
                ", regardless of crit rate or the target's crit resistance",
            ];
        },
    },
    708: {
        name: 'Final Critical Resistance',
        wikiDesc: "Adds (+) or subtracts (-) a direct amount from the target's critical resistance " +
            '(e.g. -0.2 reduces resist by a direct 20%).',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            const dir = mod < 0 ? 'reduced' : 'increased';
            return [`Critical resistance ${dir} by `, seg.pct(Math.abs(mod))];
        },
    },
    709: {
        name: 'Unify Str/Agi',
        wikiDesc: 'Equalizes Strength and Agility to the higher of the two, after stat calculation. ' +
            'Used on all Assassins for the crit/CDMG revamp.',
        describe() {
            return ['Equalizes STR and AGI to the higher of the two (applied after stat calculation)'];
        },
    },
    710: {
        name: 'Apply Skill Based on Current Hit Combo',
        wikiDesc: 'Applies a skill for every multiple of a combo count (e.g. multiples of 20); make ' +
            'comboCount negative to use the every-multiple rule instead of an exact match.',
        describe({ raw }) {
            const groups = raw.split(';').map((s) => s.trim()).filter((s) => s !== '');
            if (!groups.length) {
                return null;
            }
            const items: Segment[][] = [];
            for (const group of groups) {
                const parts = group.split(',').map((s) => s.trim());
                const comboCount = ParamReader.num(parts[0]);
                const skillId = ParamReader.num(parts[1]);
                if (isNaN(comboCount) || isNaN(skillId)) {
                    continue;
                }
                const chance = parts.length > 2 ? ParamReader.num(parts[2]) : NaN;
                const item: Segment[] = [];
                if (comboCount < 0) {
                    item.push(
                        'every ', seg.num(Math.abs(comboCount)), ' combo, applies ', seg.skill(skillId));
                } else {
                    item.push('at exactly ', seg.num(comboCount), ' combo, applies ', seg.skill(skillId));
                }
                if (!isNaN(chance)) {
                    item.push(' (', seg.pct(chance), ' chance)');
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return ['Combo-based skill application: ', seg.list(items)];
        },
    },
    711: {
        name: 'Defer Cooldown',
        wikiDesc: "Defers the skill's cooldown start until the skill's ApplyDeferredCooldown ACT " +
            'block. Param must be 1 to activate.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '') {
                return null;
            }
            if (trimmed !== '1') {
                return ['Inactive (param must be 1 to defer the cooldown)'];
            }
            return ["Defers the skill's cooldown start until its ApplyDeferredCooldown ACT block"];
        },
    },
    712: {
        name: 'Highlight Skill',
        wikiDesc: 'Highlights the listed skills on the skill bar (like state effect 345, but without ' +
            "enhancing the skill). Setting a skill's highlight color to 0 uses the default yellow.",
        describe({ raw }) {
            const f = raw.split(';').map((s) => s.trim());
            if (f.length < 2) {
                return null;
            }
            const items: Segment[][] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const skillId = ParamReader.num(f[i]);
                if (isNaN(skillId)) {
                    continue;
                }
                const color = f[i + 1];
                const item: Segment[] = [seg.skill(skillId)];
                if (!color || color === '0') {
                    item.push(' (default yellow)');
                } else {
                    item.push(` (glow #${color})`);
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return ['Highlights ', seg.list(items)];
        },
    },
    713: {
        name: 'Camera FoV',
        wikiDesc: "Multiplies the local player's camera field of view.",
        describe({ params }) {
            const mult = params.nextNum();
            if (isNaN(mult)) {
                return null;
            }
            return ["Multiplies the local player's camera field of view by ", seg.pct(mult)];
        },
    },
    714: {
        name: 'Hide HP Bar',
        wikiDesc: "Prevents the enemy's HP bar from being visible on the UI or over their head.",
        describe() {
            return ["Hides the holder's HP bar from the UI and from over their head"];
        },
    },
    715: {
        name: 'Apply Skill Upon Successful iFrame',
        wikiDesc: 'Upon a successful invincibility-frame dodge, applies the listed skills.',
        describe({ raw }) {
            const groups = raw.split(';').map((s) => s.trim()).filter((s) => s !== '');
            if (!groups.length) {
                return null;
            }
            const items: Segment[][] = [];
            for (const group of groups) {
                const parts = group.split(',').map((s) => s.trim());
                const cooldownMs = ParamReader.num(parts[0]);
                const skillId = ParamReader.num(parts[1]);
                if (isNaN(cooldownMs) || isNaN(skillId)) {
                    continue;
                }
                const chance = parts.length > 2 ? ParamReader.num(parts[2]) : NaN;
                const item: Segment[] = [seg.skill(skillId)];
                if (!isNaN(chance)) {
                    item.push(' (', seg.pct(chance), ' chance)');
                }
                if (cooldownMs > 0) {
                    item.push(' (', seg.secs(cooldownMs), ' cooldown)');
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return ['Upon a successful iFrame dodge, applies ', seg.list(items)];
        },
    },
    716: {
        name: 'Execute Bubble',
        wikiDesc: 'Fires off any bubbletable rows listening for bubbleExecId (condition 51).',
        describe({ params }) {
            const bubbleExecId = params.nextNum();
            if (isNaN(bubbleExecId)) {
                return null;
            }
            return ['Fires bubbletable rows listening for bubble execute ID ', seg.id(bubbleExecId)];
        },
    },
    717: {
        name: 'Marker',
        status: 'marker',
        wikiDesc: 'Marker state effect for Warden shards.',
    },
    718: {
        name: 'Detonate Stacks',
        wikiDesc: "Applies skillId at the target's stack level of stateEffectId (capped at the " +
            "skill's max level) if the target has stateEffectId applied by the owner of this effect " +
            '(unless ignoreOwner is set), and removes those stacks (unless skipRemove is set).',
        describe({ params }) {
            const f = params.fields();
            const stateEffectId = ParamReader.num(f[0]);
            const skillId = ParamReader.num(f[1]);
            if (isNaN(stateEffectId) || isNaN(skillId)) {
                return null;
            }
            const ignoreOwner = f[2] === '1';
            const skipRemove = f[3] === '1';
            const out: Segment[] = [
                'If the target has ', seg.blow(stateEffectId),
                ignoreOwner ? ' (applied by anyone)' : ' applied by the owner of this effect',
                ', applies ', seg.skill(skillId), ' at its stack level (capped at max skill level)',
            ];
            out.push(skipRemove ? ', keeping the stacks' : ', then removes those stacks');
            return out;
        },
    },
    719: {
        name: 'Remove Specific Blows from Specific Skills',
        wikiDesc: 'Removes the listed state effects that were applied by the listed skill IDs.',
        describe({ raw }) {
            const groups = raw.split(';').map((s) => s.trim()).filter((s) => s !== '');
            if (!groups.length) {
                return null;
            }
            const items: Segment[][] = [];
            for (const group of groups) {
                const parts = group.split(',').map((s) => s.trim());
                const skillId = ParamReader.num(parts[0]);
                if (isNaN(skillId)) {
                    continue;
                }
                const blowIds = parts.slice(1).map(ParamReader.num).filter((v) => !isNaN(v));
                if (!blowIds.length) {
                    continue;
                }
                items.push([
                    seg.skill(skillId), ': ', seg.list(blowIds.map((id) => [seg.blow(id)] as Segment[])),
                ]);
            }
            if (!items.length) {
                return null;
            }
            return ['Removes: ', seg.list(items)];
        },
    },
    720: {
        name: "Death's Dance",
        wikiDesc: 'Splits incoming damage by DmgRate%: DmgRate% is taken instantly, and the rest is ' +
            'dealt as a DoT every 0.25s over DOTDurationMs, starting after DOTDelayMs (e.g. 0.4 means ' +
            '60% taken instantly, 40% taken as DoT).',
        describe({ params }) {
            const f = params.numbers();
            const dmgRate = f[0];
            const dotDurationMs = f[1];
            const dotDelayMs = f[2];
            if (isNaN(dmgRate) || isNaN(dotDurationMs) || isNaN(dotDelayMs)) {
                return null;
            }
            return [
                'Splits incoming damage: ', seg.pct(1 - dmgRate), ' taken instantly, and ',
                seg.pct(dmgRate), ' dealt as a DoT every 0.25s over ', seg.secs(dotDurationMs),
                ', starting ', seg.secs(dotDelayMs), ' after the hit',
            ];
        },
    },
    721: {
        name: "Delay'd DoT",
        status: 'internal',
        wikiDesc: "Internal use by 720 (Death's Dance); do not use.",
    },
    722: {
        name: 'Universal DoT',
        wikiDesc: 'Highly configurable damage-over-time state effect.',
        describe({ params }) {
            const f = params.numbers();
            const tickIntervalMs = f[0];
            const flatDamage = f[1];
            const atkType = f[2];
            const atkRatioDamage = f[3];
            const targetMaxHpRatioDamage = f[4];
            const targetCurrentHpRatioDamage = f[5];
            const element = f[6];
            const useEleAtkAndDef = f[7];
            const canDetonate = f[8];
            if (isNaN(tickIntervalMs) || isNaN(flatDamage) || isNaN(atkType) || isNaN(atkRatioDamage) ||
                isNaN(targetMaxHpRatioDamage) || isNaN(targetCurrentHpRatioDamage) || isNaN(element) ||
                isNaN(useEleAtkAndDef) || isNaN(canDetonate)) {
                return null;
            }
            const parts: Segment[][] = [];
            if (flatDamage) {
                parts.push([seg.num(flatDamage, params.span(1) || undefined), ' flat']);
            }
            if (atkRatioDamage) {
                parts.push([
                    seg.pct(atkRatioDamage, 2, params.span(3) || undefined), ' of ',
                    seg.en(atkType, ATK_TYPES_722, 'unknown', params.span(2) || undefined),
                ]);
            }
            if (targetMaxHpRatioDamage) {
                parts.push([
                    seg.pct(targetMaxHpRatioDamage, 2, params.span(4) || undefined), " of target's max HP",
                ]);
            }
            if (targetCurrentHpRatioDamage) {
                parts.push([
                    seg.pct(targetCurrentHpRatioDamage, 2, params.span(5) || undefined),
                    " of target's current HP",
                ]);
            }
            const out: Segment[] = ['Every ', seg.secs(tickIntervalMs, params.span(0) || undefined), ', deals '];
            if (parts.length) {
                out.push(seg.list(parts));
            } else {
                out.push('no damage');
            }
            if (element < 0) {
                out.push(' as non-elemental damage');
            } else {
                out.push(
                    ' as ', seg.en(element, ELEMENTS_722, 'unknown', params.span(6) || undefined), ' damage');
            }
            const flags: string[] = [];
            if (useEleAtkAndDef === 1) {
                flags.push('uses elemental ATK/DEF');
            }
            if (canDetonate === 1) {
                flags.push('can be detonated');
            }
            if (flags.length) {
                out.push(' (', flags.join(', '), ')');
            }
            return out;
        },
    },
    723: {
        name: 'Vampyra Marker',
        status: 'marker',
        wikiDesc: 'Vampyra marker.',
    },
    724: {
        name: 'Knightess Marker',
        status: 'marker',
        wikiDesc: 'Knightess marker.',
    },
    725: {
        name: 'Provoke Marker',
        status: 'marker',
        wikiDesc: 'Provoke marker.',
    },
    726: {
        name: 'True Max HP Mod',
        wikiDesc: 'Final layer of max HP modifications, with layer support. Rate 0.1 = +10% max HP. ' +
            'If layer is omitted, it is layer 0.',
        describe({ params }) {
            const f = params.numbers();
            const rate = f[0];
            if (isNaN(rate)) {
                return null;
            }
            const layer = !isNaN(f[1]) ? f[1] : 0;
            const dir = rate < 0 ? 'decreased' : 'increased';
            return [
                'Max HP ', dir, ' by ', seg.pct(Math.abs(rate)), ' (layer ', seg.id(layer), ')',
            ];
        },
    },
    727: {
        name: 'Light Fury Marker',
        status: 'marker',
        wikiDesc: 'Light Fury marker.',
    },
    728: {
        name: 'Force Show Skill Cooldown',
        wikiDesc: "Forcibly shows the owning skill's cooldown on the passive skill CD bar.",
        describe() {
            return ["Forcibly shows the owning skill's cooldown on the passive skill CD bar"];
        },
    },
    729: {
        name: 'Show Crosshair Text',
        wikiDesc: "Displays the given UIString text at the player's crosshair with the given color, " +
            'which is either a #RRGGBB hex color or a named color index (0/default = green, ' +
            '1 = red, 2 = orange, 3 = blue).',
        describe({ params }) {
            const f = params.fields();
            const uiStringMid = ParamReader.num(f[0]);
            if (isNaN(uiStringMid)) {
                return null;
            }
            const hex = f[1] ? CROSSHAIR_HEX_729.exec(f[1]) : null;
            if (hex) {
                return [
                    'Displays UIString ', seg.id(uiStringMid), ' at the crosshair in ',
                    seg.val('#' + hex[1].toUpperCase()),
                ];
            }
            const colorOption = ParamReader.num(f[1]);
            const color = isNaN(colorOption) ? 0 : colorOption;
            return [
                'Displays UIString ', seg.id(uiStringMid), ' at the crosshair in ',
                seg.en(color, CROSSHAIR_COLORS_729, 'unknown'),
            ];
        },
    },
    730: {
        name: 'Ray Mechanic Marker',
        status: 'marker',
        wikiDesc: 'Ray Mechanic marker.',
    },
    731: {
        name: 'Only Hittable by Player',
        wikiDesc: 'Can only be hit by players (not summons, not monsters).',
        describe() {
            return ['Can only be hit by players (not summons, not monsters)'];
        },
    },
    732: {
        name: 'Limit Maximum Distance',
        status: 'unused',
        wikiDesc: 'Can only be hit within a certain range (not implemented yet; reserved).',
    },
    733: {
        name: 'Attacks Deal %HP',
        wikiDesc: 'Same as blow 16 (HP Restore %), but applied as a buff to the hitter: attacks deal ' +
            "Pct% of the target's HP as damage instead of restoring HP.",
        describe({ params }) {
            const f = params.numbers();
            const pct = f[0];
            if (isNaN(pct)) {
                return null;
            }
            const out: Segment[] = [
                "Attacks deal ", seg.pct(Math.abs(pct)), " of the target's HP as damage",
            ];
            if (f[1] === 1) {
                out.push(', capable of reducing HP to 0');
            }
            return out;
        },
    },
    734: {
        name: 'DMC Weapons Rank Marker',
        status: 'marker',
        wikiDesc: 'Marker for Nerdge Gacha 10-star rarity weapons.',
    },
    735: {
        name: 'Damage Multiplier Against Specific Monsters',
        wikiDesc: 'Deals modified damage against specific monster table IDs. Ratio 0.0 = no change, ' +
            '1.0 = 2x damage.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const items: Segment[][] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const monsterIds = ParamReader.numList(f[i], ',');
                const ratio = ParamReader.num(f[i + 1]);
                if (!monsterIds.length || isNaN(ratio)) {
                    continue;
                }
                items.push([
                    seg.list(monsterIds.map((id) => [seg.monster(id)] as Segment[])), ': deals ',
                    seg.pct(1 + ratio), ' damage',
                ]);
            }
            if (!items.length) {
                return null;
            }
            return ['Damage against specific monsters: ', seg.list(items)];
        },
    },
    736: {
        name: 'True Defense Mod',
        wikiDesc: 'Buff holder takes modified damage: incomingDmg × (1 + mod). See blow 50 for ' +
            'layering behavior; layer 1 is reserved for Affliction.',
        describe({ params }) {
            const f = params.numbers();
            const ratio = f[0];
            const layer = f[1];
            if (isNaN(ratio) || isNaN(layer)) {
                return null;
            }
            const dir = ratio < 0 ? 'decreased' : 'increased';
            const out: Segment[] = [
                `Incoming damage ${dir} by `, seg.pct(Math.abs(ratio)), ' (layer ', seg.id(layer), ')',
            ];
            if (layer === 1) {
                out.push(' (Affliction)');
            }
            return out;
        },
    },
    737: {
        name: 'Teleport to Projectile',
        wikiDesc: 'Upon application, teleports to the nearest instance of the given projectile. ' +
            'Actions are optional and can be left empty for no action change.',
        describe({ params }) {
            const f = params.fields();
            const projectileWeaponTableId = ParamReader.num(f[0]);
            const maxRange = ParamReader.num(f[1]);
            if (isNaN(projectileWeaponTableId) || isNaN(maxRange)) {
                return null;
            }
            const out: Segment[] = [
                'Teleports to the nearest instance of projectile ', seg.id(projectileWeaponTableId),
                ' within ', seg.num(maxRange), ' range',
            ];
            const successProjectileAction = f[2];
            const successActorAction = f[3];
            if (successProjectileAction) {
                out.push(', playing ', seg.action(successProjectileAction), ' on the projectile');
            }
            if (successActorAction) {
                out.push(' and ', seg.action(successActorAction), ' on self');
            }
            return out;
        },
    },
    738: {
        name: 'Absolute Action Speed',
        wikiDesc: 'Forcibly sets action speed (from buffs) to the given ratio. Respects time stop.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Forcibly sets action speed to ', seg.pct(ratio), ' (respects time stop)'];
        },
    },
    739: {
        name: 'Convert Overcap Crit to CDMG',
        wikiDesc: 'Converts total crit over 100% (uncapped crit stat + crit buffs) to CDMG at the ' +
            'given ratio.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ['Converts crit over 100% to CDMG at a ', seg.pct(ratio), ' ratio'];
        },
    },
    740: {
        name: 'Binding Break-Free Ease',
        wikiDesc: 'Bindings from blow 218 are easier to break free from, by Mod%: ADAD mashing is Mod% ' +
            'easier, and spacebar mechanics get a Mod% bigger window to hit correctly.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return [
                'Bindings from ', seg.blow(218), ' are ', seg.pct(mod),
                ' easier to break free from (ADAD mashing and spacebar-mechanic window)',
            ];
        },
    },
    741: {
        name: 'Set Actor Scale',
        wikiDesc: "Scales the actor's model (and hitbox?) by the given value, e.g. 1.0 = normal size.",
        describe({ params }) {
            const scale = params.nextNum();
            if (isNaN(scale)) {
                return null;
            }
            return ["Scales the actor's model (and hitbox?) to ", seg.pct(scale), ' of normal size'];
        },
    },
    742: {
        name: 'Duplicate Projectile Hit',
        wikiDesc: 'Causes hits from projectiles to hit twice but deal 10% of the original damage.',
        describe() {
            return ['Projectile hits strike twice, each dealing ', seg.pct(0.1), ' of the original damage'];
        },
    },
    743: {
        name: 'Set Combo Gain',
        wikiDesc: 'Sets the amount of combo gained per successful hit (e.g. param 2 means gain 2 ' +
            'combo per hit).',
        describe({ params }) {
            const gainAmount = params.nextNum();
            if (isNaN(gainAmount)) {
                return null;
            }
            return ['Sets combo gained per successful hit to ', seg.num(gainAmount)];
        },
    },
    744: {
        name: 'Increase Buff Duration',
        wikiDesc: 'While active, multiplies the duration of timed buffs (Buff duration type only) ' +
            'gained by the actor by 1 + pct (e.g. 0.15 = 15% longer). Stacks additively. Does not ' +
            'extend existing buffs or buffs applied by the same skill as this. By default only applies ' +
            'to self-applied buffs (applier == recipient).',
        describe({ params }) {
            const f = params.numbers();
            const pct = f[0];
            if (isNaN(pct)) {
                return null;
            }
            const scope = !isNaN(f[1]) ? f[1] : 0;
            const out: Segment[] = [
                'Timed buffs (Buff duration type) gained by the actor last ', seg.pct(1 + pct),
                ' as long (stacks additively; does not extend existing buffs or buffs applied by the ' +
                'same skill as this)',
            ];
            out.push(scope === 1 ? ' (applies to any applier)' : ' (self-applied buffs only)');
            return out;
        },
    },
    745: {
        name: 'Rhythm Minigame',
        wikiDesc: 'Roots the actor and opens a DDR-style rhythm minigame for the duration, playing ' +
            "an osu!mania (`.osu`) beatmap. The chart must have Mode 3 and CircleSize 4 (4K only); " +
            'anything else is rejected and the effect removed immediately. Clearing the chart at or ' +
            'above the pass threshold removes the effect early; falling short lets it expire ' +
            'normally. Keyboard only, no gamepad support.',
        describe({ params }) {
            // A third field (the retired scrollMs suggestion) may appear in
            // legacy rows; the client ignores it, so it is ignored here too.
            const f = params.fields(',');
            const chartFileName = f[0];
            const passThresholdPercent = ParamReader.num(f[1]);
            if (!chartFileName || isNaN(passThresholdPercent)) {
                return null;
            }
            return [
                'Opens the rhythm minigame with chart ', seg.val(chartFileName), '. Clearing it at ' +
                'or above ', seg.pct(passThresholdPercent / 100),
                ' accuracy removes the effect early; falling short lets it expire normally, keyboard only',
            ];
        },
    },
};
